import React, { useState, useContext } from 'react';
import {
    Box,
    Typography,
    Grid,
    Avatar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
} from '@mui/material';
import RichText from "../../components/RichText";
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { AuthError, withAuthCheck, fetchRaidCreate, fetchRaidLeave, fetchRaidJoin } from "../../services/api";
import { useTranslation } from 'react-i18next';

export default function V3RuinsWidget({
    bosses = [],
    location = '',
}) {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedBoss, setSelectedBoss] = useState(null);
    const [selectedTime, setSelectedTime] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [localBosses, setLocalBosses] = useState(bosses);
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [eventToLeave, setEventToLeave] = useState(null);
    const [joinDialogOpen, setJoinDialogOpen] = useState(false);
    const [eventToJoin, setEventToJoin] = useState(null);
    const token = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    // Обновляем локальное состояние при изменении props
    React.useEffect(() => {
        setLocalBosses(bosses);
    }, [bosses]);

    // Функция для проверки, участвует ли пользователь в каком-либо событии
    const isUserInAnyEvent = () => {
        return localBosses.some(boss => 
            boss.events && boss.events.some(event => 
                event.isPlayerJoined > 0 || event.isPlayerAuthor > 0
            )
        );
    };

    // Функция сортировки событий по времени
    const sortEventsByTime = (events) => {
        return events.sort((a, b) => {
            // Предполагаем, что время приходит в формате, который можно сравнить
            // Если нужно, можно добавить парсинг времени
            return a.time.localeCompare(b.time);
        });
    };

    // Функция добавления нового события к боссу
    const addEventToBoss = (bossCode, newEvent) => {
        setLocalBosses(prevBosses => {
            return prevBosses.map(boss => {
                if (boss.code === bossCode) {
                    const updatedEvents = sortEventsByTime([
                        ...(boss.events || []),
                        newEvent
                    ]);
                    return {
                        ...boss,
                        events: updatedEvents
                    };
                }
                return boss;
            });
        });
    };

    const handleOpenDialog = (boss) => {
        setSelectedBoss(boss);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedTime('');
        setMessage('');
        setError('');
    };

    const handleSubmit = async () => {
        if (!selectedTime) {
            setError(t('widgets.ruins.pleaseSelectTime'));
            return;
        }
        if (message.length > 280) {
            setError(t('widgets.ruins.maxMessageLength'));
            return;
        }
        try {
            const data = await withAuthCheck(
                fetchRaidCreate,
                token,
                selectedBoss.code,
                selectedTime,
                message,
            );
            
            // Проверяем, есть ли новое событие в ответе
            if (data.event) {
                addEventToBoss(selectedBoss.code, data.event);
            }
            
            showSnackbar(data.result, 'success');
            handleCloseDialog();
        } catch (err) {
            if (err instanceof AuthError) {
                // WebAuthForm покажется автоматически через AuthProvider
                return;
            }
            const errorMessage = err.result || err.message || 'error';
            setError(errorMessage);
            showSnackbar(errorMessage, 'error');
        }
    };

    const handleEventClick = (event) => {
        // Если пользователь уже присоединился, но не является автором - можно покинуть
        if (event.isPlayerJoined > 0 && event.isPlayerAuthor === 0) {
            setEventToLeave(event);
            setLeaveDialogOpen(true);
        }
        // Если пользователь не присоединился и не является автором и не участвует в других событиях - можно присоединиться
        else if (event.isPlayerJoined === 0 && event.isPlayerAuthor === 0 && !isUserInAnyEvent()) {
            setEventToJoin(event);
            setJoinDialogOpen(true);
        }
    };

    const handleLeaveEvent = async () => {
        if (!eventToLeave) return;
        
        try {
            const data = await withAuthCheck(
                fetchRaidLeave,
                token,
                eventToLeave.eventId,
            );
            
            // Обновляем локальное состояние - убираем галочку
            setLocalBosses(prevBosses => {
                return prevBosses.map(boss => {
                    if (boss.events) {
                        const updatedEvents = boss.events.map(event => {
                            if (event.eventId === eventToLeave.eventId) {
                                return {
                                    ...event,
                                    isPlayerJoined: 0,
                                    participantsCount: Math.max(0, event.participantsCount - 1)
                                };
                            }
                            return event;
                        });
                        return { ...boss, events: updatedEvents };
                    }
                    return boss;
                });
            });
            
            showSnackbar(data.result, 'success');
            setLeaveDialogOpen(false);
            setEventToLeave(null);
        } catch (err) {
            if (err instanceof AuthError) {
                // WebAuthForm покажется автоматически через AuthProvider
                return;
            }
            const errorMessage = err.result || err.message || 'error';
            showSnackbar(errorMessage, 'error');
        }
    };

    const handleCloseLeaveDialog = () => {
        setLeaveDialogOpen(false);
        setEventToLeave(null);
    };

    const handleJoinEvent = async () => {
        if (!eventToJoin) return;
        
        try {
            const data = await withAuthCheck(
                fetchRaidJoin,
                token,
                eventToJoin.eventId,
            );
            
            // Обновляем локальное состояние - добавляем галочку
            setLocalBosses(prevBosses => {
                return prevBosses.map(boss => {
                    if (boss.events) {
                        const updatedEvents = boss.events.map(event => {
                            if (event.eventId === eventToJoin.eventId) {
                                return {
                                    ...event,
                                    isPlayerJoined: 1,
                                    participantsCount: event.participantsCount + 1
                                };
                            }
                            return event;
                        });
                        return { ...boss, events: updatedEvents };
                    }
                    return boss;
                });
            });
            
            showSnackbar(data.result, 'success');
            setJoinDialogOpen(false);
            setEventToJoin(null);
        } catch (err) {
            if (err instanceof AuthError) {
                // WebAuthForm покажется автоматически через AuthProvider
                return;
            }
            const errorMessage = err.result || err.message || 'error';
            showSnackbar(errorMessage, 'error');
        }
    };

    const handleCloseJoinDialog = () => {
        setJoinDialogOpen(false);
        setEventToJoin(null);
    };

    return (
        <Box
            sx={{
                borderRadius: 3,
                mb: 0,
            }}
        >
            {/* Основное содержимое */}
            <Box sx={{ p: 0 }}>
                {/* Список боссов */}
                {localBosses?.length > 0 && (
                    <Box sx={{ mt: 1, px: 0, pb: 0 }}>
                        <Grid container spacing={2}>
                            {localBosses.map((boss) => (
                                <Grid item xs={12} key={boss.code}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 1,
                                            backgroundColor: '#F4F3FE',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Avatar
                                            src={`/img/mobs/${boss.icon}.png`}
                                            alt={boss.name}
                                            sx={{ width: 48, height: 48 }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {boss.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                <RichText content={boss.kind} /><br/>
                                            </Typography>
                                            <Typography variant="caption">
                                                <RichText content={boss.status} />
                                            </Typography>
                                            {boss.joined > 0 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                    <PeopleAltRoundedIcon sx={{ fontSize: 16 }} />
                                                    <Typography variant="caption">
                                                        {boss.joined}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    
                                    {/* Список событий босса */}
                                    {boss.events && boss.events.length > 0 && (
                                        <Box sx={{ mt: 1, px:1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            {boss.events.map((event) => (
                                                <Box
                                                    key={event.eventId}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 0.5,
                                                        cursor: ((event.isPlayerJoined > 0 && event.isPlayerAuthor === 0) || 
                                                                (event.isPlayerJoined === 0 && event.isPlayerAuthor === 0 && !isUserInAnyEvent())) ? 'pointer' : 'default',
                                                        '&:hover': ((event.isPlayerJoined > 0 && event.isPlayerAuthor === 0) || 
                                                                  (event.isPlayerJoined === 0 && event.isPlayerAuthor === 0 && !isUserInAnyEvent())) ? {
                                                            opacity: 0.8,
                                                            transform: 'scale(1.02)',
                                                            transition: 'all 0.2s ease-in-out'
                                                        } : {},
                                                    }}
                                                    onClick={() => handleEventClick(event)}
                                                >
                                                    {/* Аватар */}
                                                    <Avatar
                                                        src={event.orgAvaIcon ? `/img/${event.orgAvaIcon}.png` : undefined}
                                                        alt="Organizer"
                                                        sx={{ 
                                                            width: '32px',
                                                            height: '32px',
                                                        }}
                                                    />
                                                    
                                                    {/* Основная информация в обводке */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2.5,
                                                            p: 1,
                                                            border: 0,
                                                            borderRadius: 2,
                                                            backgroundColor: '#c8d6e5',
                                                        }}
                                                    >
                                                        {/* Время события */}
                                                        <Typography variant="subtitle2">
                                                            {event.time}
                                                        </Typography>
                                                        
                                                        {/* Количество участников */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                            <PeopleAltRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                            <Typography variant="caption">
                                                                {event.participantsCount}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    
                                                    {/* Индикатор участия игрока (за пределами обводки) */}
                                                    {event.isPlayerAuthor > 0 && (
                                                        <StarRoundedIcon
                                                            sx={{ 
                                                                color: '#FF9800', 
                                                                fontSize: 24
                                                            }} 
                                                        />
                                                    )}
                                                    {event.isPlayerJoined > 0 && event.isPlayerAuthor === 0 && (
                                                        <CheckCircleIcon 
                                                            sx={{ 
                                                                color: '#4CAF50', 
                                                                fontSize: 24
                                                            }} 
                                                        />
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    
                                    {/* Кнопка создания рейда */}
                                    {boss.canCreateEvent && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', px:1, mb:2, mt:1 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleOpenDialog(boss)}
                                                sx={{
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    fontSize: 16,
                                                    bgcolor: '#1DA1F2',
                                                    color: 'white',
                                                    '&:hover': {
                                                        bgcolor: '#145db2'
                                                    }
                                                }}
                                            >
                                               {t('widgets.ruins.organizeRaid')}
                                            </Button>
                                        </Box>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Box>

            {/* Диалог создания рейда */}
            <Dialog 
                open={openDialog} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('widgets.ruins.organizeRaidTitle')}</DialogTitle>
                {selectedBoss && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, pt: 1 }}>
                        <Avatar
                            src={selectedBoss.icon ? `/img/mobs/${selectedBoss.icon}.png` : undefined}
                            alt={selectedBoss.name}
                            sx={{ width: 48, height: 48 }}
                        />
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {selectedBoss.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('widgets.ruins.location')}: {location}
                            </Typography>
                        </Box>
                    </Box>
                )}
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <FormControl 
                            sx={{ mb: 2, minWidth: '80%',
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1DA1F2',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#1DA1F2',
                                }
                            }}
                        >
                            <InputLabel>{t('widgets.ruins.selectTime')}</InputLabel>
                            <Select
                                value={selectedTime}
                                onChange={(e) => setSelectedTime(e.target.value)}
                                label={t('widgets.ruins.selectTime')}
                                sx={{ minWidth: '50%',
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1DA1F2',
                                    },
                                }}
                            >
                                {selectedBoss && selectedBoss.timeSlots && selectedBoss.timeSlots.map((slot) => (
                                    <MenuItem 
                                        key={slot.value} 
                                        value={slot.value}
                                        disabled={!slot.available}
                                    >
                                        {slot.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t('widgets.ruins.messageOptional')}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            error={message.length > 280}
                            sx={{
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: message.length > 280 ? '#d32f2f' : '#1DA1F2',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: message.length > 280 ? '#d32f2f' : '#1DA1F2',
                                }
                            }}
                            inputProps={{
                                maxLength: 281
                            }}
                        />
                        {message.length > 280 && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {t('widgets.ruins.maxMessageLength')}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseDialog}
                        sx={{
                            color: '#1DA1F2',
                            backgroundColor: 'transparent',
                            fontWeight: 600,
                            fontSize: 16
                        }}
                    >
                        {t('widgets.ruins.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!selectedTime}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: 16,
                            bgcolor: '#1DA1F2',
                            color: 'white',
                            '&:hover': {
                                bgcolor: '#145db2'
                            }
                        }}
                    >
                        {t('widgets.ruins.submit')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения выхода из события */}
            <Dialog 
                open={leaveDialogOpen} 
                onClose={handleCloseLeaveDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('widgets.ruins.leaveEventTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('widgets.ruins.leaveEventMessage')}
                    </Typography>
                    {eventToLeave && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                                {t('widgets.ruins.time')}: {eventToLeave.time}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PeopleAltRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption">
                                    {eventToLeave.participantsCount} {t('widgets.ruins.participants')}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t('widgets.ruins.note')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseLeaveDialog}
                        sx={{
                            color: '#666',
                            backgroundColor: 'transparent',
                            fontWeight: 600,
                            fontSize: 16
                        }}
                    >
                        {t('widgets.ruins.cancel')}
                    </Button>
                    <Button
                        onClick={handleLeaveEvent}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: 16,
                            bgcolor: '#d32f2f',
                            color: 'white',
                            '&:hover': {
                                bgcolor: '#b71c1c'
                            }
                        }}
                    >
                        {t('widgets.ruins.leave')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения присоединения к событию */}
            <Dialog 
                open={joinDialogOpen} 
                onClose={handleCloseJoinDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('widgets.ruins.joinEventTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('widgets.ruins.joinEventMessage')}
                    </Typography>
                    {eventToJoin && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                                {t('widgets.ruins.time')}: {eventToJoin.time}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PeopleAltRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption">
                                    {eventToJoin.participantsCount} {t('widgets.ruins.participants')}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseJoinDialog}
                        sx={{
                            color: '#666',
                            backgroundColor: 'transparent',
                            fontWeight: 600,
                            fontSize: 16
                        }}
                    >
                        {t('widgets.ruins.cancel')}
                    </Button>
                    <Button
                        onClick={handleJoinEvent}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: 16,
                            bgcolor: '#4CAF50',
                            color: 'white',
                            '&:hover': {
                                bgcolor: '#388E3C'
                            }
                        }}
                    >
                        {t('widgets.ruins.join')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}